import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef
} from '@angular/core';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {Popover, PopoverRef, Position} from '@greetgo/popover';
import {BusinessFieldRef} from '../../model/BusinessFieldRef';
import {BoInstanceSelectorRecord} from '../../model/BoInstanceSelectorRecord';
import {DYNAMIC_FORMS_SERVICE, DynamicFormsService} from '../DynamicFormsService';
import {BoInstanceSelectorRequest} from '../../model/BoInstanceSelectorRequest';
import {byOrderIndexField} from '../Util';
import {BusinessObjectDetails} from '../../model/BusinessObjectDetails';
import {BoInstanceToSave} from '../../model/BoInstanceToSave';
import {FormFieldType} from '../../model/FormFieldType';
import {BoInstanceAmountToSave} from '../../model/BoInstanceAmountToSave';
import {BoInstanceSelectorConfig} from './BoInstanceSelectorConfig';
import {BoiDialogData} from '../../model/BoiDialogData';
import {SubSink} from '../../lib/SubSink';
import {filter, map, mapTo, switchMap, take} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {TableInstanceRecord} from '../../model/TableInstanceRecord';
import {BoInstanceDialogType} from '../../model/BoInstanceDialogType';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class TableComponent implements OnInit, OnChanges {
  @Input() businessFields: BusinessFieldRef[];
  @Input() chosenIds: string[];
  @Input() businessObjectId: string;
  @Input() config: BoInstanceSelectorConfig;
  @Output() boInstanceAdded = new EventEmitter<BoInstanceSelectorRecord>();
  @Output() boInstanceDeleted = new EventEmitter<BoInstanceSelectorRecord>();
  @Output() boInstanceCreated = new EventEmitter<BoInstanceToSave>();
  @Output() boiAmountChanged = new EventEmitter<BoInstanceAmountToSave>();
  @Output() boiDialogOpenEvent = new EventEmitter<BoiDialogData>();
  @Output() businessFieldToShowChanged = new EventEmitter<BusinessFieldRef>();
  @Output() costCalcEnabledChanged = new EventEmitter<boolean>();
  @Output() costCalcFieldIdChanged = new EventEmitter<string>();
  @Output() costCalcPriceLabelChanged = new EventEmitter<string>();
  private popoverRef: PopoverRef;
  private readonly formSubs = new SubSink();

  totalCost = 0;
  popoverOpened = false;
  chosenRecords: BoInstanceSelectorRecord[];
  searchBusinessFields: BusinessFieldRef[];
  priceFields: BusinessFieldRef[];
  headers: BusinessFieldRef[];
  boDetails: BusinessObjectDetails;
  boInstanceAmount = 1;

  constructor(
    @Inject(DYNAMIC_FORMS_SERVICE)
    private readonly service: DynamicFormsService,
    private readonly popover: Popover,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadByIds();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.businessFields) {
      this.refreshHeaders(changes.businessFields.currentValue);
      this.loadByIds();
    }
  }

  private loadByIds() {
    if (this.chosenIds && this.chosenIds.length > 0) {

      const request = this.asRequest(this.chosenIds);

      this.service.loadBoInstanceRecordsByIds(request).subscribe(res => {
        this.chosenRecords = res;
        this.recalculateEachRecordCost();
        this.recalculateTotalCost();
        this.cdr.detectChanges();
      }, error => {

        this.service.showAccessDeniedNotification(this.boDetails.name);
        console.log('geHX3OLnUb error', error);
      });

    } else {
      this.chosenRecords = [];
    }
  }

  private recalculateEachRecordCost() {
    this.chosenRecords.forEach(x => x.cost = x.price * x.amount);
  }

  private asRequest(chosenIds: string[]): BoInstanceSelectorRequest {
    return {
      boInstanceIds: chosenIds,
      businessObjectId: this.businessObjectId,
      fieldIds: this.headers.map(z => z.fieldId),
      ownerBoId: this.config.ownerBoId,
      ownerFieldId: this.config.ownerFieldId,
      ownerBoInstanceId: this.config.ownerBoInstanceId
    };
  }

  showPriceChooserPopover(origin: HTMLDivElement, content: TemplateRef<any>) {
    this.priceFields = this.getPriceFields();
    const position = Position.left;
    const hasBackdrop = true;
    this.popoverRef = this.popover.open({origin, content, position, hasBackdrop});
  }

  private getPriceFields() {
    return this.businessFields.filter(x => x.type === FormFieldType.BUSINESS_OBJECT || x.type === FormFieldType.INPUT_NUMBER);
  }

  showPopover(origin: HTMLDivElement, content: TemplateRef<any>) {
    this.searchBusinessFields = [...this.businessFields.filter(x => x.type !== FormFieldType.TAB_GROUP)];
    this.popoverOpened = true;
    const position = Position.left;
    const color = 'white';
    const hasBackdrop = true;
    this.popoverRef = this.popover.open({origin, content, position, color, hasBackdrop});
    this.popoverRef.beforeClosed.subscribe(() => {
      this.popoverOpened = false;
      this.cdr.detectChanges();
    });
  }

  async showCreatePopover(origin: HTMLDivElement, content: TemplateRef<any>) {
    this.boDetails = await this.service.loadBoDetails(this.businessObjectId, BoInstanceDialogType.VIEW, null);
    const position = Position.left;
    const color = 'white';
    const hasBackdrop = true;

    this.popoverRef = this.popover.open({origin, content, position, color, hasBackdrop});

  }

  filterFields(value: string) {
    this.searchBusinessFields = this.businessFields
      .filter(x => x.type !== FormFieldType.TAB_GROUP)
      .filter(x => x.label.toLowerCase().includes(value.toLowerCase()));
  }

  toggleToShow(record: BusinessFieldRef, change: MatCheckboxChange) {
    record.toShow = change.checked;
    const businessFieldRef = this.businessFields.find(x => x.fieldId === record.fieldId);
    businessFieldRef.toShow = change.checked;
    this.businessFieldToShowChanged.emit(record);
    this.refreshHeaders(this.businessFields);
    this.loadByIds();
  }

  private refreshHeaders(f: BusinessFieldRef[]) {
    this.headers = f.filter(z => z.toShow).sort(byOrderIndexField);
  }

  private recalculateTotalCost() {
    this.totalCost = 0;
    this.chosenRecords.forEach(x => this.totalCost += x.cost);
  }

  private saveAmountIfNeed(boInstanceId: string, amount?: number) {
    if (!this.config.costCalcEnabled) { return; }

    const amountToSave: BoInstanceAmountToSave = {
      boInstanceId, amount,
      ownerFieldId: this.config.ownerFieldId,
      ownerBoId: this.config.ownerBoId,
      ownerBoInstanceId: this.config.ownerBoInstanceId,
      draftId: this.config.boiDraftId
    };

    this.boiAmountChanged.emit(amountToSave);
    this.boInstanceAmount = 1;
  }

  close() {
    this.formSubs.unsubscribe();
    this.popoverRef.close();
  }

  deleteBoInstance(record: BoInstanceSelectorRecord) {
    this.boInstanceDeleted.emit(record);
    this.chosenRecords = this.chosenRecords.filter(x => x.boInstanceId !== record.boInstanceId);
  }

  // noinspection DuplicatedCode
  openBoiDetails(boInstanceId: string, type: BoInstanceDialogType) {

    const data: BoiDialogData = {
      boId: this.businessObjectId,
      boInstanceId: boInstanceId,
      type: type
    };

    this.service.openBoInstanceDialog(data)
      .pipe(
        take(1),
        switchMap(handleCanceled),
        filter(filterUndefined),
        map(setReturnRecordFieldIds),
        switchMap(saveBoInstance),
        switchMap(applyDraft),
      ).subscribe(onDraftApplied, onError);

    this.boiDialogOpenEvent.emit(data);

    const component = this;

    function filterUndefined(x): boolean {

      return !!x;

    }

    function handleCanceled(toSave: BoInstanceToSave): Observable<BoInstanceToSave> {
      if (!toSave || type === BoInstanceDialogType.VIEW) {
        return of(undefined);
      }

      if (toSave.fields) {
        return of(toSave);
      }

      return component.service.removeDraft(toSave.draftId).pipe(mapTo(undefined));

    }

    function setReturnRecordFieldIds(toSave: BoInstanceToSave): BoInstanceToSave {
      toSave.returnRecordFieldIds = component.headers.map(x => x.fieldId);
      return toSave;
    }

    function saveBoInstance(toSave: BoInstanceToSave): Observable<[BoInstanceToSave, TableInstanceRecord]> {

      return component.service.saveBoInstance(toSave).pipe(map(tableRec => [toSave, tableRec]));

    }

    function applyDraft([toSave, tableRec]: [BoInstanceToSave, TableInstanceRecord]): Observable<TableInstanceRecord> {

      return component.service.applyAndRemoveDraft(toSave.draftId).pipe(map(() => tableRec));

    }

    function onDraftApplied(tableRec: TableInstanceRecord): void {

      if (type !== BoInstanceDialogType.EDIT) { return; }

      const rec = component.chosenRecords.find(x => x.boInstanceId = tableRec.instanceId);
      rec.displayValues = tableRec.values.map(value => value.displayValue);

    }

    function onError(err: Error) {

      component.service.showAccessDeniedNotification(err.message);

    }

  }

  openBoiDetailsForEdit(boInstanceId: string): void {

    this.openBoiDetails(boInstanceId, BoInstanceDialogType.EDIT);

  }

  openBoiDetailsForView(boInstanceId: string): void {

    this.openBoiDetails(boInstanceId, BoInstanceDialogType.VIEW);

  }

  onCostCalcEnabledChanged(checkboxChange: MatCheckboxChange) {
    if (checkboxChange.checked) {
      this.onCostCalcPriceLabelChanged('Цена');
    }
    this.costCalcEnabledChanged.emit(checkboxChange.checked);
  }

  onCostCalcFieldIdChanged(record: BusinessFieldRef) {
    this.onCostCalcPriceLabelChanged(record.label);
    this.costCalcFieldIdChanged.emit(record.fieldId);
  }

  onCostCalcPriceLabelChanged(value: string) {
    this.costCalcPriceLabelChanged.emit(value);
  }

  get headerLength() {
    if (!this.config.costCalcEnabled) {
      return this.headers.length;
    } else {
      return this.headers.length + 3;
    }
  }

  filterPriceFields(value: string) {

    this.priceFields = this.getPriceFields().filter(x => x.label.toLowerCase().includes(value.toLowerCase()));

  }

  saveRowAmount(row: BoInstanceSelectorRecord) {
    this.saveAmountIfNeed(row.boInstanceId, row.amount);
    this.recalculateEachRecordCost();
    this.recalculateTotalCost();
  }

  onBoInstanceAdded(rec: BoInstanceSelectorRecord) {
    this.chosenRecords.push(rec);
    this.recalculateEachRecordCost();
    this.recalculateTotalCost();
    this.boInstanceAdded.emit(rec);
  }

  onOverlayCloseClick() {
    this.popoverRef.close();
  }

  onOverlaySaveClick() {
    this.popoverRef.close();
  }
}
